/**
 * globalChatContextCore — the ONE chat context object, and the hook that reads
 * it without requiring a Provider.
 *
 * WHY THIS IS ITS OWN FILE (PR-S, 2026-09-22). `Settings.tsx` only needs to
 * READ the context (`useMaybeGlobalChatContext`), but it used to import it
 * from `GlobalChatContext.tsx`, whose Provider value-imports `useGlobalChat`
 * (~112 KB, and ~40 more chat files behind it). Settings is on the first-paint
 * path, so that one hook dragged the chat engine onto every signed-out
 * visitor's first load. Reading a context needs nothing but the context.
 *
 * THERE MUST BE EXACTLY ONE `createContext` for the chat. `GlobalChatContext.tsx`
 * imports `GlobalChatContext` from here rather than creating its own — two
 * context objects would mean the Provider publishes into one and the hooks read
 * the other, so every consumer would silently see `null`. Pinned by
 * tests/hooks/chatContextIsOneObject.test.ts.
 */

import { createContext, useContext } from 'react';
import type { GlobalChatContextValue } from './types';

export const GlobalChatContext = createContext<GlobalChatContextValue | null>(null);

/**
 * useMaybeGlobalChatContext is similar to useGlobalChatContext but returns null
 * instead of throwing if used outside a provider. Useful for components that
 * may or may not be within a chat context.
 */
export function useMaybeGlobalChatContext(): GlobalChatContextValue | null {
  return useContext(GlobalChatContext);
}
